import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Phone, Mail, MessageCircle, Calendar, FileText, Eye } from "lucide-react";

const ESTATUS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-800" },
  { value: "contactado", label: "Cliente contactado", color: "bg-blue-100 text-blue-800" },
  { value: "recompra", label: "Recompra confirmada", color: "bg-emerald-100 text-emerald-800" },
  { value: "descartado", label: "Descartado", color: "bg-stone-100 text-stone-700" },
];

const Seguimientos = () => {
  const [seguimientos, setSeguimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstatus, setFilterEstatus] = useState("todos");
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [detalleData, setDetalleData] = useState(null);
  const [editEstatus, setEditEstatus] = useState("pendiente");
  const [editNotas, setEditNotas] = useState("");
  const [editProxima, setEditProxima] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/seguimientos`);
      setSeguimientos(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Error al cargar seguimientos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const abrirDetalle = async (seg) => {
    setSeleccionado(seg);
    setEditEstatus(seg.estatus || "pendiente");
    setEditNotas(seg.notas || "");
    setEditProxima(seg.proxima_oportunidad || "");
    setDetalleOpen(true);
    try {
      const { data } = await axios.get(`${API}/seguimientos/${seg.seguimiento_id}`);
      setDetalleData(data);
    } catch {
      toast.error("Error al cargar detalle");
    }
  };

  const guardar = async () => {
    if (!seleccionado) return;
    try {
      await axios.put(`${API}/seguimientos/${seleccionado.seguimiento_id}`, {
        estatus: editEstatus,
        notas: editNotas,
        proxima_oportunidad: editProxima,
      });
      toast.success("Seguimiento actualizado");
      setDetalleOpen(false);
      fetchData();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const filtrados = seguimientos.filter(s =>
    filterEstatus === "todos" ? true : s.estatus === filterEstatus
  );

  const stats = {
    total: seguimientos.length,
    pendientes: seguimientos.filter(s => s.estatus === "pendiente").length,
    recompra: seguimientos.filter(s => s.estatus === "recompra").length,
  };

  return (
    <div className="space-y-6" data-testid="seguimientos-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-stone-900">Seguimiento Post-Evento</h1>
        <p className="text-sm text-stone-500 mt-1">Mantén la relación con tus clientes para impulsar la recompra</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Total</p>
            <p className="font-display text-2xl text-stone-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Pendientes</p>
            <p className="font-display text-2xl text-amber-600">{stats.pendientes}</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Recompra</p>
            <p className="font-display text-2xl text-emerald-700">{stats.recompra}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <Select value={filterEstatus} onValueChange={setFilterEstatus}>
          <SelectTrigger className="w-full md:w-64 border-stone-300" data-testid="filter-estatus-seguimientos">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {ESTATUS.map(e => (
              <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-stone-500">Cargando...</div>
      ) : filtrados.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-600">Aún no hay seguimientos. Cuando un evento se marque como &quot;Finalizado&quot;, aparecerá aquí automáticamente.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtrados.map(s => {
            const badge = ESTATUS.find(e => e.value === s.estatus) || ESTATUS[0];
            return (
              <Card key={s.seguimiento_id} className="border-stone-200 hover:shadow-md transition-shadow" data-testid={`seguimiento-${s.seguimiento_id}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-stone-900 truncate">{s.cliente_nombre || "Cliente"}</h3>
                      <p className="text-sm text-stone-500 truncate">
                        {s.tipo_evento || "Evento"} · {s.fecha_evento || "Sin fecha"}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${badge.color} whitespace-nowrap`}>{badge.label}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-stone-600">
                    {s.cliente_telefono && (
                      <a href={`tel:${s.cliente_telefono}`} className="flex items-center gap-1 hover:text-emerald-700" data-testid={`tel-${s.seguimiento_id}`}>
                        <Phone className="w-3 h-3" /> {s.cliente_telefono}
                      </a>
                    )}
                    {s.cliente_whatsapp && (
                      <a
                        href={`https://wa.me/${s.cliente_whatsapp.replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-emerald-700"
                        data-testid={`wa-${s.seguimiento_id}`}
                      >
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </a>
                    )}
                    {s.cliente_correo && (
                      <a href={`mailto:${s.cliente_correo}`} className="flex items-center gap-1 hover:text-emerald-700">
                        <Mail className="w-3 h-3" /> Email
                      </a>
                    )}
                  </div>
                  {s.notas && (
                    <p className="text-sm text-stone-700 bg-stone-50 p-2 rounded line-clamp-2">{s.notas}</p>
                  )}
                  {s.proxima_oportunidad && (
                    <p className="text-xs text-emerald-700 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Próxima: {s.proxima_oportunidad}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-stone-300"
                    onClick={() => abrirDetalle(s)}
                    data-testid={`btn-ver-${s.seguimiento_id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" /> Ver detalle y editar
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={detalleOpen} onOpenChange={setDetalleOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-detalle-seguimiento">
          <DialogHeader>
            <DialogTitle>{seleccionado?.cliente_nombre || "Seguimiento"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Estado</Label>
                <Select value={editEstatus} onValueChange={setEditEstatus}>
                  <SelectTrigger className="border-stone-300" data-testid="select-estatus-seguimiento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTATUS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Próxima oportunidad</Label>
                <Input
                  value={editProxima}
                  onChange={(e) => setEditProxima(e.target.value)}
                  placeholder="ej: Cumpleaños 2027 o XV años hermana"
                  className="border-stone-300"
                  data-testid="input-proxima-oportunidad"
                />
              </div>
            </div>
            <div>
              <Label>Notas y conversación</Label>
              <Textarea
                value={editNotas}
                onChange={(e) => setEditNotas(e.target.value)}
                rows={4}
                placeholder="¿Cómo le fue al cliente? ¿Quedó satisfecho? ¿Próximo evento?"
                className="border-stone-300"
                data-testid="textarea-notas-seguimiento"
              />
            </div>

            {detalleData?.eventos_historicos?.length > 0 && (
              <div>
                <Label className="mb-2 block">Eventos históricos del cliente ({detalleData.eventos_historicos.length})</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {detalleData.eventos_historicos.map(ev => (
                    <div key={ev.evento_id} className="text-sm bg-stone-50 p-2 rounded flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{ev.nombre || ev.tipo}</p>
                        <p className="text-xs text-stone-500">{ev.fecha} · {ev.estatus}</p>
                      </div>
                      <FileText className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detalleData?.seguimiento?.historial?.length > 0 && (
              <div>
                <Label className="mb-2 block">Historial</Label>
                <div className="space-y-1 max-h-32 overflow-y-auto text-xs text-stone-600">
                  {detalleData.seguimiento.historial.map((h, i) => (
                    <div key={i} className="bg-stone-50 p-2 rounded">
                      <span className="font-medium">{h.accion}</span> · {(h.fecha || "").slice(0, 10)}
                      {h.detalle && <p className="text-stone-500">{h.detalle}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalleOpen(false)} className="border-stone-300" data-testid="btn-cerrar-seguimiento">Cerrar</Button>
            <Button onClick={guardar} className="bg-emerald-900 hover:bg-emerald-800 text-white" data-testid="btn-guardar-seguimiento">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Seguimientos;
